import _ from 'lodash';
import { ReactNode, useMemo, useContext, createContext } from 'react';

// Import types
import { CalculatedPriceProduct } from 'types/api/packetsSubscription';
import type { PacketWithPriceAndChannels } from 'types/views/configurator';

// Import hooks
import { usePremiumPacketsSummary } from 'components/views/offer_configurator/hooks';
import { useConfiguratorContext } from 'components/context/configurator/ConfiguratorProvider';

// Import helpers
import { checkIsIncluded, getCalculatedPrice } from 'components/views/offer_configurator/helpers/packets';

type Props = {
	children: ReactNode;
	provider: string;
	packets: PacketWithPriceAndChannels[];
	onPacketSelect?: (packet: PacketWithPriceAndChannels) => void;
};

type ContextType = Omit<Props, 'children'> & {
	prices: Array<CalculatedPriceProduct | undefined>;
	isOwned: boolean;
	isSelected: boolean;
	isPricesLoading: boolean;
	isIncludedSelectedMain: boolean;
	isIncludedInCurrentMain: boolean;
	till: string | undefined;
	isAdditionalProductsAction: boolean;
	packetsUuid: string[];
	isSubscriptionActive: boolean;
	isDisabled: boolean;
};

export const PacketsGroupContext = createContext<ContextType>({} as ContextType);

export const usePacketsGroupContext = () => useContext(PacketsGroupContext);

const PacketsGroupProvider = ({ children, packets, provider, onPacketSelect }: Props) => {
	const { action, premium, calculated } = usePremiumPacketsSummary();

	const { subscription, selectedMain, currentMain } = useConfiguratorContext();

	const isAdditionalProductsAction = action === 'add_additional_products';

	const isPricesLoading = !calculated.length;

	const prices = useMemo(() => {
		const prices = packets.map((packet) => getCalculatedPrice(packet.uuid, calculated));
		return _.sortBy(prices, 'current_price');
	}, [isPricesLoading]);

	const isOwned = useMemo(() => packets.some(({ uuid }) => checkIsIncluded(uuid, subscription?.premium)), [subscription]);

	const isIncludedSelectedMain = useMemo(
		() => packets.some(({ uuid }) => checkIsIncluded(uuid, selectedMain?.included_packets_products)),
		[selectedMain?.uuid]
	);

	const isIncludedInCurrentMain = useMemo(
		() => packets.some(({ uuid }) => checkIsIncluded(uuid, currentMain?.included_packets_products)),
		[currentMain?.uuid]
	);

	const isSelected = useMemo(() => packets.some(({ uuid }) => premium.some((packet) => packet.uuid === uuid)), [premium]);

	const packetsUuid = packets.map((packet) => packet.uuid);

	return (
		<PacketsGroupContext.Provider
			value={{
				prices,
				packets,
				provider,
				isOwned,
				isSelected,
				isPricesLoading,
				isIncludedSelectedMain,
				isIncludedInCurrentMain,
				till: subscription?.till,
				isAdditionalProductsAction,
				packetsUuid,
				onPacketSelect,
				isSubscriptionActive: Boolean(subscription?.isActive),
				isDisabled: isOwned || isIncludedSelectedMain || isIncludedInCurrentMain
			}}
		>
			{children}
		</PacketsGroupContext.Provider>
	);
};

export default PacketsGroupProvider;
